import { format, parseISO, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import numeral from 'numeral';
import { CURRENCY, DATE_FORMATS } from './constants';

// =====================================================
// FORMATADORES DE MOEDA
// =====================================================

/**
 * Formata valor monetário
 */
export const formatCurrency = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return 'R$ 0,00';
  
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numericValue)) return 'R$ 0,00';
  
  return new Intl.NumberFormat(CURRENCY.LOCALE, {
    style: 'currency',
    currency: CURRENCY.CODE,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numericValue);
};

/**
 * Formata valor monetário compacto (1.5K, 2.3M)
 */
export const formatCurrencyCompact = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return 'R$ 0';
  
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numericValue)) return 'R$ 0';
  
  return new Intl.NumberFormat(CURRENCY.LOCALE, {
    style: 'currency',
    currency: CURRENCY.CODE,
    notation: 'compact',
    compactDisplay: 'short',
  }).format(numericValue);
};

/**
 * Converte string de moeda para número
 */
export const parseCurrency = (value: string): number => {
  if (!value) return 0;
  
  // Remove tudo exceto números, vírgula e ponto
  const cleanValue = value.replace(/[^\d,.-]/g, '');
  
  // Se termina com vírgula seguida de 2 dígitos, é decimal brasileiro
  if (/,\d{2}$/.test(cleanValue)) {
    return parseFloat(cleanValue.replace(/\./g, '').replace(',', '.'));
  }
  
  // Se termina com ponto seguido de 2 dígitos, é decimal americano
  if (/\.\d{2}$/.test(cleanValue)) {
    return parseFloat(cleanValue.replace(/,/g, ''));
  }
  
  // Caso padrão: assume formato brasileiro
  return parseFloat(cleanValue.replace(/\./g, '').replace(',', '.')) || 0;
};

// =====================================================
// FORMATADORES DE DATA
// =====================================================

/**
 * Formata data para exibição
 */
export const formatDate = (
  date: string | Date | null | undefined,
  formatString: string = DATE_FORMATS.DEFAULT
): string => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    
    if (!isValid(dateObj)) return '';
    
    return format(dateObj, formatString, { locale: ptBR });
  } catch {
    return '';
  }
};

/**
 * Formata data e hora
 */
export const formatDateTime = (date: string | Date | null | undefined): string => {
  return formatDate(date, DATE_FORMATS.WITH_TIME);
};

/**
 * Formata data para input HTML
 */
export const formatDateForInput = (date: string | Date | null | undefined): string => {
  return formatDate(date, DATE_FORMATS.ISO);
};

/**
 * Formata data relativa (há 2 horas, ontem, etc.)
 */
export const formatRelativeDate = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    
    if (!isValid(dateObj)) return '';
    
    const now = new Date();
    const diffInMs = now.getTime() - dateObj.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    
    if (diffInMinutes < 1) return 'agora mesmo';
    if (diffInMinutes < 60) return `há ${diffInMinutes} min`;
    if (diffInHours < 24) return `há ${diffInHours}h`;
    if (diffInDays < 7) return `há ${diffInDays} dias`;
    
    return formatDate(date);
  } catch {
    return '';
  }
};

// =====================================================
// FORMATADORES DE DOCUMENTO
// =====================================================

/**
 * Valida CPF
 */
export const validateCPF = (cpf: string): boolean => {
  if (!cpf) return false;
  
  const numbers = cpf.replace(/\D/g, '');
  
  if (numbers.length !== 11) return false;
  
  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1+$/.test(numbers)) return false;
  
  // Validação do primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(numbers.charAt(i)) * (10 - i);
  }
  let remainder = 11 - (sum % 11);
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(numbers.charAt(9))) return false;
  
  // Validação do segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(numbers.charAt(i)) * (11 - i);
  }
  remainder = 11 - (sum % 11);
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(numbers.charAt(10))) return false;
  
  return true;
};

/**
 * Valida CNPJ
 */
export const validateCNPJ = (cnpj: string): boolean => {
  if (!cnpj) return false;
  
  const numbers = cnpj.replace(/\D/g, '');
  
  if (numbers.length !== 14) return false;
  
  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1+$/.test(numbers)) return false;
  
  // Validação do primeiro dígito verificador
  let sum = 0;
  let weight = 5;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(numbers.charAt(i)) * weight;
    weight = weight === 2 ? 9 : weight - 1;
  }
  let remainder = sum % 11;
  const digit1 = remainder < 2 ? 0 : 11 - remainder;
  if (digit1 !== parseInt(numbers.charAt(12))) return false;
  
  // Validação do segundo dígito verificador
  sum = 0;
  weight = 6;
  for (let i = 0; i < 13; i++) {
    sum += parseInt(numbers.charAt(i)) * weight;
    weight = weight === 2 ? 9 : weight - 1;
  }
  remainder = sum % 11;
  const digit2 = remainder < 2 ? 0 : 11 - remainder;
  if (digit2 !== parseInt(numbers.charAt(13))) return false;
  
  return true;
};

/**
 * Formata CPF
 */
export const formatCPF = (cpf: string | null | undefined): string => {
  if (!cpf) return '';
  
  const numbers = cpf.replace(/\D/g, '');
  
  if (numbers.length !== 11) return cpf;
  
  return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

/**
 * Formata CNPJ
 */
export const formatCNPJ = (cnpj: string | null | undefined): string => {
  if (!cnpj) return '';
  
  const numbers = cnpj.replace(/\D/g, '');
  
  if (numbers.length !== 14) return cnpj;
  
  return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

/**
 * Formata documento (CPF ou CNPJ automaticamente)
 */
export const formatDocument = (document: string | null | undefined): string => {
  if (!document) return '';
  
  const numbers = document.replace(/\D/g, '');
  
  if (numbers.length === 11) return formatCPF(numbers);
  if (numbers.length === 14) return formatCNPJ(numbers);
  
  return document;
};

/**
 * Mascara documento durante digitação
 */
export const maskDocument = (value: string): string => {
  const numbers = value.replace(/\D/g, '');
  
  if (numbers.length <= 11) {
    // Máscara de CPF
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2');
  } else {
    // Máscara de CNPJ
    return numbers
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})/, '$1-$2');
  }
};

// =====================================================
// FORMATADORES DE TELEFONE
// =====================================================

/**
 * Formata telefone
 */
export const formatPhone = (phone: string | null | undefined): string => {
  if (!phone) return '';
  
  const numbers = phone.replace(/\D/g, '');
  
  if (numbers.length === 10) {
    return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  
  if (numbers.length === 11) {
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  
  return phone;
};

/**
 * Formata telefone para chave PIX
 */
export const formatPhoneForPix = (phone: string | null | undefined): string => {
  if (!phone) return '';
  
  const numbers = phone.replace(/\D/g, '');
  
  if (numbers.length >= 10) {
    return `+55${numbers}`;
  }
  
  return phone;
};

// =====================================================
// FORMATADORES DE CEP
// =====================================================

/**
 * Formata CEP
 */
export const formatCEP = (cep: string | null | undefined): string => {
  if (!cep) return '';
  
  const numbers = cep.replace(/\D/g, '');
  
  if (numbers.length === 8) {
    return numbers.replace(/(\d{5})(\d{3})/, '$1-$2');
  }
  
  return cep;
};

// =====================================================
// FORMATADORES BANCÁRIOS
// =====================================================

/**
 * Formata linha digitável de boleto
 */
export const formatDigitableLine = (line: string | null | undefined): string => {
  if (!line) return '';
  
  const numbers = line.replace(/\D/g, '');
  
  if (numbers.length === 47) {
    return numbers.replace(
      /(\d{5})(\d{5})(\d{5})(\d{6})(\d{5})(\d{6})(\d{1})(\d{14})/,
      '$1.$2 $3.$4 $5.$6 $7 $8'
    );
  }
  
  return line;
};

/**
 * Formata código de barras
 */
export const formatBarcode = (barcode: string | null | undefined): string => {
  if (!barcode) return '';
  
  const numbers = barcode.replace(/\D/g, '');
  
  if (numbers.length === 44) {
    return numbers.replace(/(\d{4})(\d{5})(\d{10})(\d{10})(\d{15})/, '$1 $2 $3 $4 $5');
  }
  
  return barcode;
};

/**
 * Formata agência bancária
 */
export const formatAgency = (agency: string | null | undefined): string => {
  if (!agency) return '';
  
  const numbers = agency.replace(/\D/g, '');
  
  if (numbers.length === 4) {
    return numbers;
  }
  
  if (numbers.length === 5) {
    return numbers.replace(/(\d{4})(\d{1})/, '$1-$2');
  }
  
  return agency;
};

/**
 * Formata conta bancária
 */
export const formatAccount = (account: string | null | undefined, digit?: string): string => {
  if (!account) return '';
  
  if (digit) {
    return `${account}-${digit}`;
  }
  
  // Se já tem dígito
  if (account.includes('-')) {
    return account;
  }
  
  return account;
};

// =====================================================
// FORMATADORES DE NÚMEROS
// =====================================================

/**
 * Formata número com separadores de milhares
 */
export const formatNumber = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return '0';
  
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numericValue)) return '0';
  
  return new Intl.NumberFormat(CURRENCY.LOCALE).format(numericValue);
};

/**
 * Formata porcentagem
 */
export const formatPercentage = (
  value: number | string | null | undefined,
  decimals: number = 2
): string => {
  if (value === null || value === undefined) return '0%';
  
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numericValue)) return '0%';
  
  return new Intl.NumberFormat(CURRENCY.LOCALE, {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(numericValue / 100);
};

/**
 * Formata tamanho de arquivo
 */
export const formatFileSize = (bytes: number | null | undefined): string => {
  if (!bytes || bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// =====================================================
// FORMATADORES DE TEXTO
// =====================================================

/**
 * Capitaliza primeira letra
 */
export const capitalize = (text: string | null | undefined): string => {
  if (!text) return '';
  
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

/**
 * Capitaliza cada palavra
 */
export const capitalizeWords = (text: string | null | undefined): string => {
  if (!text) return '';
  
  return text.replace(/\w\S*/g, (word) => 
    word.charAt(0).toUpperCase() + word.substr(1).toLowerCase()
  );
};

/**
 * Trunca texto
 */
export const truncateText = (text: string | null | undefined, maxLength: number): string => {
  if (!text) return '';
  
  if (text.length <= maxLength) return text;
  
  return text.slice(0, maxLength) + '...';
};

/**
 * Remove acentos
 */
export const removeAccents = (text: string | null | undefined): string => {
  if (!text) return '';
  
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

/**
 * Slug (URL-friendly)
 */
export const slugify = (text: string | null | undefined): string => {
  if (!text) return '';
  
  return removeAccents(text)
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, '') // Remove caracteres especiais
    .replace(/\s+/g, '-') // Substitui espaços por hífens
    .replace(/-+/g, '-') // Remove hífens duplicados
    .trim();
};

// =====================================================
// VALIDADORES
// =====================================================

/**
 * Valida CPF
 */
export const isValidCPF = (cpf: string | null | undefined): boolean => {
  if (!cpf) return false;
  
  const numbers = cpf.replace(/\D/g, '');
  
  if (numbers.length !== 11) return false;
  
  // Verifica se não é uma sequência
  if (/^(\d)\1{10}$/.test(numbers)) return false;
  
  // Validação dos dígitos verificadores
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(numbers.charAt(i)) * (10 - i);
  }
  
  let digit = 11 - (sum % 11);
  if (digit === 10 || digit === 11) digit = 0;
  if (digit !== parseInt(numbers.charAt(9))) return false;
  
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(numbers.charAt(i)) * (11 - i);
  }
  
  digit = 11 - (sum % 11);
  if (digit === 10 || digit === 11) digit = 0;
  if (digit !== parseInt(numbers.charAt(10))) return false;
  
  return true;
};

/**
 * Valida CNPJ
 */
export const isValidCNPJ = (cnpj: string | null | undefined): boolean => {
  if (!cnpj) return false;
  
  const numbers = cnpj.replace(/\D/g, '');
  
  if (numbers.length !== 14) return false;
  
  // Verifica se não é uma sequência
  if (/^(\d)\1{13}$/.test(numbers)) return false;
  
  // Validação dos dígitos verificadores
  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(numbers.charAt(i)) * weights1[i];
  }
  
  let digit = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (digit !== parseInt(numbers.charAt(12))) return false;
  
  sum = 0;
  for (let i = 0; i < 13; i++) {
    sum += parseInt(numbers.charAt(i)) * weights2[i];
  }
  
  digit = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (digit !== parseInt(numbers.charAt(13))) return false;
  
  return true;
};

/**
 * Valida email
 */
export const isValidEmail = (email: string | null | undefined): boolean => {
  if (!email) return false;
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Valida chave PIX
 */
export const isValidPixKey = (key: string | null | undefined, type?: string): boolean => {
  if (!key) return false;
  
  switch (type) {
    case 'cpf':
      return isValidCPF(key);
    case 'cnpj':
      return isValidCNPJ(key);
    case 'email':
      return isValidEmail(key);
    case 'phone':
      const phoneNumbers = key.replace(/\D/g, '');
      return phoneNumbers.length >= 10 && phoneNumbers.length <= 11;
    case 'evp':
      // Chave aleatória: UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      return uuidRegex.test(key);
    default:
      // Auto-detect
      return (
        isValidCPF(key) ||
        isValidCNPJ(key) ||
        isValidEmail(key) ||
        (key.replace(/\D/g, '').length >= 10 && key.replace(/\D/g, '').length <= 11)
      );
  }
};